import { formatMoney } from '@/shared/billing/currency';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { PanelPago } from '@/domain/entities/PanelPago';
import { httpClient } from '@/infrastructure/http/client/httpClient';

/** Comprobante adjunto (subido por la clínica al informar el pago): vive en R2 con prefijo billing/. */
const hasAttachedReceipt = (key: string | null | undefined): key is string =>
  typeof key === 'string' && key.startsWith('billing/');

interface PaymentHistoryModalProps {
  isOpen: boolean;
  tenant: TenantGridDTO;
  onClose: () => void;
}

export const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({
  isOpen,
  tenant,
  onClose,
}) => {
  const { t } = useTranslation();
  const { error: showError, success } = useToast();
  const [payments, setPayments] = useState<PanelPago[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openingReciboId, setOpeningReciboId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPayments();
    }
  }, [isOpen, tenant]);

  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const tenantPayments = await repository.getByTenantId(tenant.id);
      // Ordenar por fecha de período (más reciente primero)
      tenantPayments.sort((a, b) => {
        const dateA = new Date(a.fechaPeriodoPago).getTime();
        const dateB = new Date(b.fechaPeriodoPago).getTime();
        return dateB - dateA;
      });
      setPayments(tenantPayments);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al cargar historial');
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      payment.fechaPeriodoPago.toLowerCase().includes(term) ||
      payment.estadoPagoDescripcion.toLowerCase().includes(term) ||
      payment.monto.toString().includes(term) ||
      (payment.notas && payment.notas.toLowerCase().includes(term))
    );
  });

  // Totales en la moneda de la factura más reciente; no se suman montos de otra moneda.
  const monedaTotales = filteredPayments[0]?.moneda ?? 'ARS';
  const totalRecaudado = filteredPayments
    .filter(p => p.fechaPago && p.moneda === monedaTotales)
    .reduce((sum, p) => sum + p.monto, 0);

  const totalPendiente = filteredPayments
    .filter(p => !p.fechaPago && p.moneda === monedaTotales)
    .reduce((sum, p) => sum + p.monto, 0);

  // Facturas individuales: la deuda es la suma de las facturas impagas (sin arrastre).
  const deudaAcumulada = filteredPayments
    .filter(p => !p.fechaPago && p.moneda === monedaTotales)
    .reduce((sum, p) => sum + (p.deudaAcumulada || 0), 0);

  const getStatusBadgeClass = (estado: string) => {
    if (estado === 'Abonado') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (estado === 'Mora') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (estado === 'Impago') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (estado === 'Gratis') return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const openRecibo = async (paymentId: number) => {
    setOpeningReciboId(paymentId);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const html = await repository.getReciboHtml(paymentId);
      const win = window.open('', '_blank');
      if (!win) {
        showError(t('pages.tenants.reciboPopupBlocked'));
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      success(t('pages.tenants.reciboOpened'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.reciboError'));
    } finally {
      setOpeningReciboId(null);
    }
  };

  const openAttachedReceipt = async (key: string) => {
    // Pestaña abierta antes del await para que no la bloquee el navegador.
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    try {
      const res = await httpClient.get<{ url: string }>('/panel-pagos/comprobante-url', { params: { key } });
      if (win) win.location.href = res.data.url;
      else window.open(res.data.url, '_blank');
    } catch {
      win?.close();
      showError(t('pages.tenants.openReceiptFileError'));
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="!max-w-5xl"
      title={`${t('pages.tenants.paymentHistory')} - ${tenant.nombre}`}
      subtitle={
        <>
          <strong>{t('pages.tenants.totalDebt')}:</strong> {formatMoney(deudaAcumulada, monedaTotales, { decimals: false })}
        </>
      }
      footer={
        <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
          {t('common.close')}
        </Button>
      }
    >
            {/* Métricas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.tenants.totalCollected')}</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  {formatMoney(totalRecaudado, monedaTotales, { decimals: false })}
                </p>
              </div>
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.tenants.totalPending')}</p>
                <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                  {formatMoney(totalPendiente, monedaTotales, { decimals: false })}
                </p>
              </div>
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('pages.tenants.totalPayments')}</p>
                <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                  {filteredPayments.length}
                </p>
              </div>
            </div>

            {/* Búsqueda */}
            <div className="mb-4">
              <Input
                label={t('common.search')}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('pages.tenants.searchPayments')}
              />
            </div>

            {/* Tabla de pagos */}
            {isLoading ? (
              <div className="text-center py-4">
                <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                {t('pages.tenants.noPayments')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-slate-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.paymentPeriod')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.paymentDate')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.amount')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.plan')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.paymentMethod')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.notes')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        {t('pages.tenants.receipt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredPayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {payment.fechaPeriodoPago}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.fechaPago 
                            ? new Date(payment.fechaPago).toLocaleDateString('es-AR')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {formatMoney(payment.monto, payment.moneda, { decimals: false })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.planId || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.medioPagoDescripcion || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.estadoPagoDescripcion)}`}>
                            {payment.estadoPagoDescripcion}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {payment.notas || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={openingReciboId === payment.id}
                            onClick={() => openRecibo(payment.id)}
                            title={t('pages.tenants.openRecibo')}
                          >
                            {openingReciboId === payment.id ? '…' : '📄'}
                          </Button>
                          {hasAttachedReceipt(payment.nombreArchivoComprobante) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void openAttachedReceipt(payment.nombreArchivoComprobante!)}
                              title={t('pages.tenants.openReceiptFile')}
                            >
                              📎
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
    </ModalShell>
  );
};
